import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection,
  query,
  orderBy,
  updateDoc,
  doc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { Edit, Calendar } from "lucide-react";
import Loader from "./Loader";
import Notification from "./Notification";
import { ChevronUp, ChevronDown } from "lucide-react";
import { exportEntriesToJson } from "./exportEntries";
import { exportEntriesToExcel } from "./exportEntriesToExcel";

function ExportButton({ entriesToExport }) {
  return (
    <button
      onClick={() => exportEntriesToJson(entriesToExport)}
      className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 transition"
    >
      Eksportuj wpisy do JSON
    </button>
  );
}

function ExportExcelButton({ entriesToExport }) {
  return (
    <button
      onClick={() => exportEntriesToExcel(entriesToExport)}
      className="bg-yellow-500 text-white py-2 px-4 rounded hover:bg-yellow-600 transition"
      title="Eksportuj wpisy do Excel"
    >
      Eksportuj wpisy do Excel
    </button>
  );
}

// ---- DYNAMICZNE MAPOWANIE SEKCJI I PYTAŃ Z GOALS ----
function buildSectionsFromGoals(goals, sections) {
  return (sections || []).map((sec) => ({
    title: sec,
    fields: goals
      .filter((g) => g.category === sec)
      .map((g) => ({
        label: g.question,
        name: g.uid,
        type: g.entryType || g.type || "number",
      })),
  }));
}

function History() {
  const [entries, setEntries] = useState([]);
  const [goals, setGoals] = useState([]);
  const [sections, setSections] = useState([]);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editData, setEditData] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const firstDayOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1
  )
    .toISOString()
    .split("T")[0];

  const [exportStartDate, setExportStartDate] = useState(firstDayOfMonth);
  const [exportEndDate, setExportEndDate] = useState(today);

  useEffect(() => {
    const loadGoals = async () => {
      try {
        const goalsSnap = await getDoc(doc(db, "goals", "currentGoals"));
        if (goalsSnap.exists()) {
          const d = goalsSnap.data();
          setGoals(d.goals || []);
          setSections(d.sections || []);
        }
      } catch (error) {
        console.error("Błąd podczas pobierania goals:", error);
      }
    };

    loadGoals();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "entries"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (querySnapshot) => {
      const fetched = [];
      querySnapshot.forEach((docSnap) => {
        fetched.push({ id: docSnap.id, ...docSnap.data() });
      });
      setEntries(fetched);
    });

    return () => unsub();
  }, []);

  const dynamicSections = buildSectionsFromGoals(goals, sections);

  const filteredEntriesForExport = entries.filter((entry) => {
    const entryDate =
      entry.createdAt && entry.createdAt.toDate
        ? entry.createdAt.toDate()
        : new Date(entry.date);

    const start = new Date(exportStartDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(exportEndDate);
    end.setHours(23, 59, 59, 999);

    return entryDate >= start && entryDate <= end;
  });

  const groupedEntries = entries.reduce((acc, entry) => {
    const dateObj =
      entry.createdAt && entry.createdAt.toDate
        ? entry.createdAt.toDate()
        : new Date(entry.date);

    const year = dateObj.getFullYear();
    const month = dateObj.toLocaleString("default", { month: "long" });
    const groupKey = `${month} ${year}`;

    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(entry);

    return acc;
  }, {});

  const months = {
    styczeń: 0,
    luty: 1,
    marzec: 2,
    kwiecień: 3,
    maj: 4,
    czerwiec: 5,
    lipiec: 6,
    sierpień: 7,
    wrzesień: 8,
    październik: 9,
    listopad: 10,
    grudzień: 11,
  };

  const sortedGroupKeys = Object.keys(groupedEntries).sort((a, b) => {
    const [monthA, yearA] = a.split(" ");
    const [monthB, yearB] = b.split(" ");
    const dateA = new Date(Number(yearA), months[monthA], 1);
    const dateB = new Date(Number(yearB), months[monthB], 1);
    return dateB - dateA;
  });

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const handleEditClick = (entry, e) => {
    if (e) e.stopPropagation();
    setEditingEntryId(entry.id);
    setEditData(entry);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveEdit = async () => {
    setIsLoading(true);
    try {
      const entryDoc = doc(db, "entries", editingEntryId);
      await updateDoc(entryDoc, { ...editData, updatedAt: new Date() });

      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === editingEntryId ? { ...entry, ...editData } : entry
        )
      );

      setEditingEntryId(null);
      setEditData({});
      setNotification("Wpis zaktualizowany!");
    } catch (error) {
      console.error("Błąd podczas aktualizacji wpisu:", error);
      setNotification("Błąd podczas aktualizacji wpisu");
    } finally {
      setIsLoading(false);
      setTimeout(() => setNotification(""), 3000);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const cancelEdit = () => {
    setEditingEntryId(null);
    setEditData({});
  };

  const renderEntryEditCommon = (entry) => (
    <div
      key={entry.id}
      className="border border-gray-300 dark:border-gray-600 p-4 rounded-lg shadow mb-4 bg-white dark:bg-gray-800"
    >
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Edycja – {entry.date}
      </h3>

      {dynamicSections.map((section) => (
        <div key={section.title} className="mb-6">
          <h4 className="text-lg font-bold text-gray-900 dark:text-white border-b pb-2 mb-2">
            {section.title}
          </h4>

          {section.fields.map((field) => {
            const { label, name, type } = field;
            if (name === "date") return null;

            if (type === "textarea") {
              return (
                <div key={name} className="mb-4">
                  <label className="block text-gray-800 dark:text-gray-200 font-medium mb-1">
                    {label}
                  </label>
                  <textarea
                    name={name}
                    value={editData[name] || ""}
                    onChange={handleEditChange}
                    rows="5"
                    className="w-full p-2 min-h-[120px] resize-y bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-800 dark:border-gray-600"
                  />
                </div>
              );
            }

            if (type === "number") {
              return (
                <div
                  key={name}
                  className="mb-4 grid grid-cols-[120px_1fr] gap-2 items-center"
                >
                  <div className="text-gray-800 dark:text-gray-200 font-medium">
                    {label}
                  </div>
                  <div className="relative sm:text-right">
                    <input
                      type="number"
                      name={name}
                      value={editData[name] || ""}
                      onChange={handleEditChange}
                      min="0"
                      className="w-20 h-8 p-1 pr-8 text-lg bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-800 dark:border-gray-600"
                    />
                    <div className="absolute inset-y-0 right-0 flex flex-col items-center justify-center pr-1">
                      <button
                        type="button"
                        onClick={() => {
                          const current = Number(editData[name] || 0);
                          setEditData((prev) => ({
                            ...prev,
                            [name]: current + 1,
                          }));
                        }}
                        className="p-1 text-emerald-600 hover:text-emerald-800"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const current = Number(editData[name] || 0);
                          if (current > 0) {
                            setEditData((prev) => ({
                              ...prev,
                              [name]: current - 1,
                            }));
                          }
                        }}
                        className="p-1 text-emerald-600 hover:text-emerald-800"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            if (type === "checkbox") {
              return (
                <div
                  key={name}
                  className="mb-4 grid grid-cols-[120px_auto] gap-2 items-center"
                >
                  <div className="text-gray-800 dark:text-gray-200 font-medium">
                    {label}
                  </div>
                  <div className="flex justify-end">
                    <input
                      type="checkbox"
                      name={name}
                      checked={editData[name] || false}
                      onChange={handleEditChange}
                      className="transform scale-125"
                      style={{ accentColor: "#FFD700" }}
                    />
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>
      ))}

      <div className="sticky bottom-0 left-0 right-0 bg-white/70 dark:bg-gray-800/70 p-4 flex justify-center items-center z-50">
        <button
          onClick={saveEdit}
          className="bg-emerald-500 text-white px-6 py-2 rounded-lg hover:bg-emerald-600 transition border-2 border-green-500 focus:outline-none mr-4"
          title="Zapisz"
        >
          Zapisz
        </button>
        <button
          onClick={cancelEdit}
          className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition focus:outline-none"
          title="Anuluj"
        >
          Anuluj
        </button>
      </div>
    </div>
  );

  const renderEntrySummaryDesktop = (entry) =>
    editingEntryId === entry.id ? (
      renderEntryEditCommon(entry)
    ) : (
      <div
        key={entry.id}
        className="border border-gray-300 dark:border-gray-600 p-4 rounded-lg shadow mb-4 bg-white dark:bg-gray-800 flex justify-between items-center"
      >
        <div className="flex items-center gap-2">
          <Calendar className="text-emerald-600" size={20} />
          <span className="text-xl font-bold text-gray-900 dark:text-white">
            AI Journal – {entry.date}
          </span>
        </div>
        <button
          onClick={(e) => handleEditClick(entry, e)}
          className="p-2 rounded-lg hover:bg-emerald-200 transition duration-200"
          title="Edytuj"
        >
          <Edit size={20} className="text-emerald-500" />
        </button>
      </div>
    );

  const renderEntryCardMobile = (entry) =>
    editingEntryId === entry.id ? (
      renderEntryEditCommon(entry)
    ) : (
      <div
        key={entry.id}
        className="border border-gray-300 dark:border-gray-600 p-4 rounded-lg shadow bg-white dark:bg-gray-800 flex justify-between items-center"
      >
        <span className="text-xl font-bold text-gray-900 dark:text-white">
          AI Journal – {entry.date}
        </span>
        <button
          onClick={() => handleEditClick(entry)}
          className="p-2 rounded-lg hover:bg-emerald-200 transition duration-200"
          title="Edytuj"
        >
          <Edit size={20} className="text-emerald-500" />
        </button>
      </div>
    );

  const renderGroupDesktop = (groupKey) => (
    <div key={groupKey} className="mb-8">
      <div
        className="flex items-center justify-between p-4 border-b border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-emerald-50 transition"
        onClick={() => toggleGroup(groupKey)}
      >
        <div className="flex items-center gap-2">
          <Calendar className="text-emerald-600" size={20} />
          <h2 className="text-xl font-bold text-emerald-800">{groupKey}</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-emerald-600">
            {groupedEntries[groupKey].length} wpisów
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleGroup(groupKey);
            }}
            className="p-1 rounded hover:bg-emerald-200 transition"
            title="Toggle Group"
          >
            {expandedGroups[groupKey] ? (
              <ChevronUp className="text-emerald-600" size={20} />
            ) : (
              <ChevronDown className="text-emerald-600" size={20} />
            )}
          </button>
        </div>
      </div>

      {expandedGroups[groupKey] && (
        <div className="space-y-4 p-4">
          {groupedEntries[groupKey]
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map((entry) => renderEntrySummaryDesktop(entry))}
        </div>
      )}
    </div>
  );

  const renderDesktop = () => (
    <div className="hidden sm:block">
      {sortedGroupKeys.map((groupKey) => renderGroupDesktop(groupKey))}
    </div>
  );

  const renderMobile = () => (
    <div className="block sm:hidden">
      {sortedGroupKeys.map((groupKey) => (
        <div key={groupKey} className="mb-6">
          <div
            className="p-4 flex justify-between items-center cursor-pointer hover:bg-emerald-50 transition"
            onClick={() => toggleGroup(groupKey)}
          >
            <div className="flex items-center gap-2">
              <Calendar className="text-emerald-600" size={20} />
              <h2 className="text-xl font-bold text-emerald-800">{groupKey}</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-emerald-600">
                {groupedEntries[groupKey].length} wpisów
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(groupKey);
                }}
                className="p-1 rounded hover:bg-emerald-200 transition"
                title="Toggle Group"
              >
                {expandedGroups[groupKey] ? (
                  <ChevronUp className="text-emerald-600" size={20} />
                ) : (
                  <ChevronDown className="text-emerald-600" size={20} />
                )}
              </button>
            </div>
          </div>

          {expandedGroups[groupKey] &&
            groupedEntries[groupKey]
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .map((entry) => renderEntryCardMobile(entry))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="px-4 py-6 max-w-full sm:max-w-2xl mx-auto">
      {isLoading && <Loader />}
      {notification && <Notification message={notification} />}

      <h2 className="text-2xl font-bold text-center text-emerald-800 dark:text-green-100 mb-6">
        Historia Wpisów
      </h2>

      <div className="mb-6 flex flex-col items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Data od
            </label>
            <input
              type="date"
              value={exportStartDate}
              onChange={(e) => setExportStartDate(e.target.value)}
              className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Data do
            </label>
            <input
              type="date"
              value={exportEndDate}
              onChange={(e) => setExportEndDate(e.target.value)}
              className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-2">
          <ExportButton entriesToExport={filteredEntriesForExport} />
          <ExportExcelButton entriesToExport={filteredEntriesForExport} />
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-300">
          Liczba wpisów do eksportu: {filteredEntriesForExport.length}
        </p>
      </div>

      {renderDesktop()}
      {renderMobile()}
    </div>
  );
}

export default History;